import { useEffect, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { SignupFlow } from "../../components/signup/SignupFlow";
import {
  useGetOrCreateSession,
  useResolveExistingAccountConflict,
} from "../../services/signup";
import { SignupState, STEPS } from "../../components/signup/types";
import useMetaTracking, {
  META_PIXEL_ID,
} from "../../components/hooks/useMetaTracking";
import useGoogleTracking, {
  GOOGLE_TAG_ID,
  getSignupConversionLabel,
  googleEventNameAccountCreation,
  googleEventNamePageLoad,
} from "../../components/hooks/useGoogleTracking";
import { useNavigate } from "react-router";
import {
  getPathToStep,
  SIGNUP_FLOW,
  FREE_SIGNUP_FLOW,
  GET_STARTED_FLOW,
} from "../../components/signup/flow";
import { buildStateFromProto } from "../../components/signup/utils";
import Loading from "../../components/common/Loading";
import TokenStore from "../../services/TokenStore";
import AuthService from "../../services/auth";

const getTrackingParams = (search: string, hash: string) => {
  const trackingParams: { [key: string]: string } = {};
  const addParams = (params: URLSearchParams) => {
    params.forEach((value, key) => {
      if (key in trackingParams) {
        trackingParams[key] = `${trackingParams[key]} ${value}`;
      } else {
        trackingParams[key] = `${value}`;
      }
    });
  };
  addParams(new URLSearchParams(search));
  if (hash.startsWith("#")) {
    addParams(new URLSearchParams(hash.slice(1)));
  }
  return trackingParams;
};

type SignupVariant = "signup" | "join" | "get-started";

export default ({
  noBillingFlow = false,
  variant,
}: {
  noBillingFlow?: boolean;
  variant?: SignupVariant;
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isDebug = !!new URLSearchParams(location.search).get("debug");
  const flowVariant: SignupVariant =
    variant || (noBillingFlow ? "join" : "signup");
  const selectedFlow =
    flowVariant === "get-started"
      ? GET_STARTED_FLOW
      : flowVariant === "join"
        ? FREE_SIGNUP_FLOW
        : SIGNUP_FLOW;
  const [initialState, setInitialState] = useState<
    [STEPS | undefined, SignupState] | null
  >(null);
  const { request, loading } = useGetOrCreateSession();
  const { request: resolveExistingAccountConflict } =
    useResolveExistingAccountConflict();
  const { track: trackMeta } = useMetaTracking(META_PIXEL_ID);
  const { fireConversionEvent, firePageLoadEvent } =
    useGoogleTracking(GOOGLE_TAG_ID);
  const updateStep = useCallback(
    (step: string) => {
      const currentStep = params.step;
      if (currentStep !== step) {
        navigate(`/${flowVariant}/${step}${location.search}`, {
          replace: false,
        });
      }
    },
    [params.step, navigate, flowVariant, location.search],
  );
  const onSignupComplete = (completionStateName: string) => {
    fireConversionEvent(
      getSignupConversionLabel(completionStateName),
      googleEventNameAccountCreation(completionStateName),
    );
    trackMeta("CompleteRegistration");
    // trackTikTok(TikTokEvents.CompleteRegistration);
  };
  // Note: we fire this request from the webflow page to start our tracking.
  useEffect(() => {
    const currentStep = params.step;
    const fetch = async () => {
      const resolveToken = queryParams.get("resolveToken");
      if (flowVariant === "get-started" && resolveToken) {
        try {
          const resp = await resolveExistingAccountConflict({
            token: resolveToken,
          });
          if (resp?.signupToken) {
            await AuthService.signupTokenLogin(resp.signupToken);
            navigate(`/get-started/congrats`, { replace: true });
            const sessionResp = await request({
              trackingParameters: {},
              token: "",
              isSignupFlow: true,
              isLandingPage: false,
            });
            const signupState: SignupState = sessionResp?.state
              ? buildStateFromProto(sessionResp.state)
              : {};
            setInitialState([STEPS.GET_STARTED_CONGRATS, signupState]);
          } else {
            const params = new URLSearchParams();
            if (resp?.resumePhone) params.set("phone", resp.resumePhone);
            if (resp?.resumeEmail) params.set("email", resp.resumeEmail);
            const qs = params.size ? `?${params.toString()}` : "";
            navigate(`/get-started/profile${qs}`, { replace: true });
          }
        } catch {
          navigate(`/get-started/profile`, { replace: true });
        }
        return;
      }

      // Re-entrant nux token
      const token = queryParams.get("token");
      // The calendar step receives Google OAuth callback params (code, scope, state)
      // in the URL — don't send those as tracking parameters.
      const trackingParams =
        currentStep === STEPS.GET_STARTED_CALENDAR
          ? {}
          : getTrackingParams(location.search, location.hash);

      const resp = await request({
        trackingParameters: trackingParams,
        token: token || "",
        isSignupFlow: true,
        isLandingPage: false,
      });
      if (!resp) return;
      let initialStep: STEPS | undefined;
      const signupState: SignupState = resp.state
        ? buildStateFromProto(resp.state)
        : {};

      if (trackingParams.button) {
        signupState.buttonParam = trackingParams.button;
      }
      const endStep =
        (params.step &&
          Object.values(STEPS).includes(params.step as STEPS) &&
          params.step) ||
        resp?.state?.stepName;
      // Load the current state, find current step, find all steps going back to the route by following back steps. Then
      // validate each step along the way going forward. Stop at the first invalid step, when it matches the current step
      // from the url, or when it matches the step from the code.
      if (endStep) {
        const path = getPathToStep(selectedFlow, endStep as STEPS);
        for (const step of path) {
          initialStep = step.name;
          if (step.isValid && !step.isValid(signupState)) {
            break;
          }
          if (initialStep === endStep) {
            break;
          }
        }
      }
      setInitialState([initialStep, signupState]);
    };
    const allowedLoggedInSteps = [
      "congrats",
      "calendar",
      "address",
      "partner",
      "referral-source",
      "account-details",
    ];
    if (
      TokenStore.getLocalCurrentUser() &&
      !(
        flowVariant === "get-started" &&
        currentStep &&
        allowedLoggedInSteps.includes(currentStep)
      )
    ) {
      navigate(`/home?from=${flowVariant}`);
      return;
    }
    if (isDebug) {
      setInitialState([undefined, {}]);
    } else {
      fetch();
    }
  }, [selectedFlow, flowVariant]);

  useEffect(() => {
    firePageLoadEvent(googleEventNamePageLoad(flowVariant));
  }, [flowVariant]);

  if (!initialState || loading) {
    return <Loading />;
  }
  const [step, formData] = initialState;
  return (
    <SignupFlow
      signupFlow={selectedFlow}
      initialStep={step}
      initialFormData={formData}
      setStepName={updateStep}
      isDebug={isDebug}
      onSignupComplete={onSignupComplete}
      stepIndicator={flowVariant === "join" ? "dots" : "bar"}
    />
  );
};
